import { useDocumentReferences } from '@ctw/shared/api/fhir/document';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  type Evidence,
  type NewGapType,
  type RiskGapModel,
  type RiskGapType,
  isRiskGapUserStatus,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { notify } from '@ctw/shared/components/toast';
import {
  ClinicalObjectGroup,
  type ClinicalObjectGroupItem,
} from '@ctw/shared/content/clinical-object/clinical-object-group';
import { RiskGapDetailsCard } from '@ctw/shared/content/risk/risk-gap-details-card';
import { RiskGapDisclaimer } from '@ctw/shared/content/risk/risk-gap-disclaimer';
import { RiskGapFeedbackCard } from '@ctw/shared/content/risk/risk-gap-feedback-card';
import { riskGapStatusBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-status-badge-definition';
import { RiskGapStatusForm } from '@ctw/shared/content/risk/risk-gap-status-form';
import {
  fetchResourceByTypeAndIds,
  useResources,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import { useCTW } from '@ctw/shared/context/ctw-context';
import type { CtwFetchFn } from '@ctw/shared/context/ctw-context';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import {
  type UseResourceDrawerOpenDrawerArgs,
  useResourceDrawer,
} from '@ctw/shared/context/resource-drawer-context';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { TelemetryBoundary, useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { useQueryClient } from '@tanstack/react-query';
import type { DiagnosticReport, DocumentReference, Resource } from 'fhir/r4';
import { compact, snakeCase, startCase } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

const IMAGE_RE = /^image\//i;
const PDF_RE = /^application\/.*(pdf|fdf)$/i;

export const measureReportDrawerAction: UseResourceDrawerOpenDrawerArgs<'MeasureReport'>['action'] =
  ({ model, patient, ctwFetch, requestContext, queryClient, reloadResource }) => {
    return {
      label: riskGapTypeToTitle(model.type),
      title: model.title,
      badges: [riskGapStatusBadgeDefinition(model)],
      content: {
        body: (
          <TelemetryBoundary
            boundaryName="MeasureReportDrawerAction"
            initialContext={{
              riskGapStatus: model.status,
              riskGapSubstatuses: model.substatuses,
              riskGapType: model.type,
              riskGapSource: model.gapSource,
              riskGapConfidenceLevel: model.confidenceLevel,
            }}
          >
            {({ telemetry }) => (
              <div className={tw`space-y-2`}>
                <RiskGapDetailsCard riskGap={model} />
                {isRiskGapUserStatus(model.status) && (
                  <RiskGapStatusForm
                    initialStatus={model.status}
                    initialSubstatuses={model.substatuses}
                    initialNotes={model.lastStatusUpdate?.note ?? ''}
                    onSubmit={(data) => {
                      return new Promise((resolve, reject) => {
                        void ctwFetch(
                          `${getStandaloneBaseUrl(requestContext.env)}/api/risk-gaps/${model.id}/status-updates`,
                          {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${requestContext.authToken}`,
                            },
                            body: JSON.stringify({
                              data: {
                                type: 'risk-gap-status-update',
                                attributes: {
                                  status: data.status,
                                  substatuses: data.substatuses,
                                  note: data.notes,
                                },
                              },
                            }),
                          },
                        )
                          .then((fetchResponse: Awaited<ReturnType<CtwFetchFn>>) => {
                            if (fetchResponse.ok) {
                              notify({
                                type: 'success',
                                title: 'The status has been updated.',
                              });
                            } else {
                              telemetry.trackError({
                                error: new Error('Error updating Risk Gap status'),
                                context: {
                                  data: JSON.stringify(fetchResponse.data),
                                  headers: JSON.stringify(fetchResponse.headers),
                                },
                              });
                              notify({
                                type: 'error',
                                title: 'Unable to update the risk gap status.',
                                body: 'Please try again later or contact support if the issue persists.',
                              });
                            }
                            void queryClient.invalidateQueries();
                            void reloadResource();
                            resolve();
                          })
                          .catch((error) => {
                            telemetry.trackError({
                              error,
                            });
                            notify({
                              type: 'error',
                              title: 'Unable to update the risk gap status.',
                              body: 'Please try again later or contact support if the issue persists.',
                            });
                            reject();
                          });
                      });
                    }}
                  />
                )}
                <MeasureReportClinicalEvidence riskGap={model} patient={patient} />
                <MeasureReportRelatedRecords riskGap={model} patient={patient} />
                <RiskGapFeedbackCard riskGap={model} patient={patient} />
                <div className={tw`flex flex-col gap-4 px-4`}>
                  <RiskGapDisclaimer />
                </div>
              </div>
            )}
          </TelemetryBoundary>
        ),
      },
    };
  };

interface MeasureReportClinicalEvidenceProps {
  riskGap: RiskGapModel;
  patient: PatientModel;
}

const MeasureReportClinicalEvidence = ({
  riskGap,
  patient,
}: MeasureReportClinicalEvidenceProps) => {
  const telemetry = useTelemetry();
  const { requestContext, ctwFetch, graphqlClient } = useCTW();
  const queryClient = useQueryClient();
  const { openResourceModal } = useResourceModal();
  const { pushView } = useResourceDrawer();
  const [loading, setLoading] = useState(true);
  const [evidenceItems, setEvidenceItems] = useState<Array<ClinicalObjectGroupItem>>([]);

  const patientContext = usePatientContextIfAvailable();
  // biome-ignore lint/correctness/useExhaustiveDependencies: Key off `riskGap.id` instead of `riskGap.evidence` to avoid unnecessary re-renders
  const documentIds = useMemo(
    () =>
      riskGap.evidence
        .map((evidence) =>
          evidence.resourceType === 'DocumentReference' ? evidence.resourceId : null,
        )
        .filter(Boolean) as Array<string>,
    [riskGap.id],
  );

  const documentReferencesQuery = useDocumentReferences(patient.UPID ?? '', documentIds);

  type ClinicalEvidenceResource = {
    evidence: Evidence;
    resource: FHIRModel<Resource> | null;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Key off `riskGap.id` instead of `riskGap.evidence` to avoid unnecessary re-renders
  useEffect(() => {
    if (documentIds.length === 0 || Array.isArray(documentReferencesQuery.data)) {
      const evidenceResourcePromises: Array<Promise<ClinicalEvidenceResource>> =
        riskGap.evidence.map((evidence) => {
          if (!(evidence.resourceType && evidence.resourceId)) {
            return Promise.resolve({ evidence, resource: null });
          }
          return new Promise((resolve) => {
            void fetchResourceByTypeAndIds({
              ctwFetch,
              requestContext,
              graphqlClient,
              queryClient,
              telemetry,
              patient,
              resourceType: evidence.resourceType as never,
              resourceIds: [evidence.resourceId],
            })
              .then((fetchedResources) => {
                resolve({ evidence, resource: fetchedResources[0] });
              })
              .catch(() => resolve({ evidence, resource: null }));
          });
        });

      Promise.allSettled(evidenceResourcePromises).then((results) => {
        setEvidenceItems(
          results
            .map((result) => (result.status === 'fulfilled' ? result.value : undefined))
            .filter((val: ClinicalEvidenceResource | undefined) => typeof val === 'object')
            .map((evidenceResource) =>
              createClinicalObjectGroupItem(evidenceResource.evidence, evidenceResource.resource),
            ),
        );
        setLoading(false);
      });
    }
  }, [
    requestContext,
    ctwFetch,
    graphqlClient,
    patient,
    telemetry,
    riskGap.id,
    documentReferencesQuery.data,
  ]);

  interface DisplayDateForRiskGapRelatedResourceArgs {
    riskGap: RiskGapModel;
    resourceModel: FHIRModel<Resource>;
  }

  const displayDateForRiskGapRelatedResource = ({
    riskGap,
    resourceModel,
  }: DisplayDateForRiskGapRelatedResourceArgs): string | undefined => {
    if (riskGap.gapSource === 'zus-suspect-model') {
      return riskGap.suspectedDisplay;
    }

    // NOTE: This only grabs zus defined gap sources right now, and does not support
    // payer defined gaps or undefined gapSource. Probably need to build onto this in the future
    // to support other gap sources.
    if (riskGap.gapSource === 'zus-recapture-model') {
      const rawRecorded = (resourceModel.resource as unknown as Record<string, string>)
        .recordedDate;
      return rawRecorded ? (formatFHIRDate(rawRecorded) ?? rawRecorded) : undefined;
    }

    return undefined;
  };

  const createClinicalObjectGroupItemForDocRef = (
    evidence: Evidence,
    resource: FHIRModel<Resource>,
  ): ClinicalObjectGroupItem => {
    const doc = resource.resource as DocumentReference;
    const docContentType = doc.content?.find((c) => c.attachment?.contentType)?.attachment
      ?.contentType;

    let badgeLabel = 'Document';
    if (docContentType && IMAGE_RE.test(docContentType)) {
      badgeLabel = 'Image';
    }
    if (docContentType && PDF_RE.test(docContentType)) {
      badgeLabel = 'PDF';
    }

    const documentReferenceForEvidence = documentReferencesQuery.data?.find(
      (docRef) => docRef.id === resource.id,
    );

    return {
      id: resource.id,
      title: resource.title ?? evidence.description,
      descriptors: [
        {
          content: displayDateForRiskGapRelatedResource({ riskGap, resourceModel: resource }),
        },
      ],
      telemetryContext: {
        riskGapEvidenceResourceId: resource.id,
        riskGapEvidenceResourceType: resource.resourceType,
      },
      content: evidence.description === resource.title ? undefined : evidence.description,
      ...(documentReferenceForEvidence && {
        onClick: () => {
          if (patientContext) {
            openResourceModal({
              telemetry,
              model: documentReferenceForEvidence,
            });
          } else {
            window.open(
              pathWithParameters(`/patients/${patient.UPID}/overview`, {
                resourceType: documentReferenceForEvidence.resourceType,
                resourceId: documentReferenceForEvidence.id,
                ...(patient.builderId && { builderId: patient.builderId }),
              }),
              '_blank',
            );
          }
        },
      }),
      ...(docContentType &&
        badgeLabel && {
          tags: [
            {
              variant: 'meta',
              label: badgeLabel,
            },
          ],
        }),
    };
  };

  const createClinicalObjectGroupItemForGenericFhirResource = (
    evidence: Evidence,
    resource: FHIRModel<Resource>,
  ): ClinicalObjectGroupItem => {
    let label: string;

    switch (resource.resourceType) {
      case 'DiagnosticReport': {
        const dr = resource.resource as DiagnosticReport;
        const categoryDisplay = dr?.category?.[0]?.text ?? dr?.category?.[0]?.coding?.[0]?.display;
        label = categoryDisplay ?? 'Diagnostic Report';
        break;
      }
      case 'MedicationStatement':
        label = 'Medication';
        break;
      case 'Condition':
        label = 'Condition';
        break;
      default:
        label =
          resource.resourceTypeTitle ??
          (resource.resourceType ? startCase(resource.resourceType) : undefined) ??
          'Evidence';
    }
    return {
      id: resource.id,
      title: resource.title ?? evidence.description,
      descriptors: [
        {
          content: `Last Updated ${displayDateForRiskGapRelatedResource({ riskGap, resourceModel: resource })}`,
        },
      ],
      telemetryContext: {
        riskGapEvidenceResourceId: resource.id,
        riskGapEvidenceResourceType: resource.resourceType,
      },
      content:
        evidence.description === resource.title || label === 'Condition' ? undefined : (
          <span className="flex flex-col">{evidence.description}</span>
        ),
      onClick: () => {
        if (resource.resourceType === 'DocumentReference') {
          if (patientContext) {
            openResourceModal({
              telemetry,
              model: resource as DocumentModel,
            });
          } else {
            window.open(
              pathWithParameters(`/patients/${patient.UPID}/overview`, {
                modalResourceType: resource.resourceType,
                modalResourceId: resource.id,
                builderId: patient.builderId,
              }),
              '_blank',
            );
          }
        } else if (patientContext) {
          pushView({
            patient,
            resourceType: resource.resourceType,
            resourceId: resource.id,
          });
        } else {
          window.open(
            pathWithParameters(`/patients/${patient.UPID}/overview`, {
              resourceType: resource.resourceType,
              resourceId: resource.id,
              builderId: patient.builderId,
            }),
            '_blank',
          );
        }
      },
    };
  };

  const createClinicalObjectGroupItem = (
    evidence: Evidence,
    resource: FHIRModel<Resource> | null,
  ): ClinicalObjectGroupItem => {
    if (!resource) {
      return {
        id: snakeCase(evidence.description),
        title: evidence.description,
        telemetryContext: {
          riskGapEvidenceResourceId: evidence.resourceId,
          riskGapEvidenceResourceType: evidence.resourceType,
        },
      };
    }

    if (resource.resourceType === 'DocumentReference') {
      return createClinicalObjectGroupItemForDocRef(evidence, resource);
    }
    return createClinicalObjectGroupItemForGenericFhirResource(evidence, resource);
  };

  return (
    <ClinicalObjectGroup
      items={evidenceItems}
      loading={loading}
      focalHeader={true}
      label="Clinical Evidence"
    />
  );
};

interface MeasureReportRelatedRecordsProps {
  riskGap: RiskGapModel;
  patient: PatientModel;
}

const MeasureReportRelatedRecords = ({ riskGap, patient }: MeasureReportRelatedRecordsProps) => {
  const { pushView } = useResourceDrawer();
  const relatedRecordsQuery = useResources({
    identifiers: riskGap.relatedRecords,
    patient,
  });
  const { openResourceModal } = useResourceModal();
  const telemetry = useTelemetry();

  const patientContext = usePatientContextIfAvailable();

  return (
    <ClinicalObjectGroup
      label="Related Records"
      focalHeader={true}
      loading={relatedRecordsQuery.isLoading}
      items={compact(
        (relatedRecordsQuery.data ?? []).map((relatedRecord) => ({
          id: relatedRecord.id,
          label: relatedRecord.resourceTypeTitle,
          title: relatedRecord.title,
          onClick: () => {
            if (relatedRecord.resourceType === 'DocumentReference') {
              if (patientContext) {
                openResourceModal({
                  telemetry,
                  model: relatedRecord as DocumentModel,
                });
              } else {
                window.open(
                  pathWithParameters(`/patients/${patient.UPID}/overview`, {
                    resourceType: relatedRecord.resourceType,
                    resourceId: relatedRecord.id,
                    builderId: patient.builderId,
                  }),
                  '_blank',
                );
              }
            } else if (patientContext) {
              pushView({
                patient,
                resourceType: relatedRecord.resourceType,
                resourceId: relatedRecord.id,
              });
            } else {
              window.open(
                pathWithParameters(`/patients/${patient.UPID}/overview`, {
                  resourceType: relatedRecord.resourceType,
                  resourceId: relatedRecord.id,
                  builderId: patient.builderId,
                }),
                '_blank',
              );
            }
          },
        })),
      )}
    />
  );
};

// riskGapTypeToTitle converts the type of a risk gap to an overview title
function riskGapTypeToTitle(type: RiskGapType | NewGapType) {
  switch (type) {
    case 'recapture':
      return 'Recaptured Condition';
    case 'suspect':
      return 'Suspected Condition';
    case 'net-new':
      return 'Newly Diagnosed Condition';
    default:
      return 'Risk Gap Condition';
  }
}
